import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Check, Download, ExternalLink, Package, Search, Shield, ShoppingCart, Tag } from 'lucide-react';
import { apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function EAStorePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('all');

  useEffect(() => {
    apiGet(API_ROUTES.public.eaStore, { auth: false })
      .then((data) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesSearch = !q
        || item.name?.toLowerCase().includes(q)
        || item.short_description?.toLowerCase().includes(q)
        || (Array.isArray(item.tags) && item.tags.some((tag) => String(tag).toLowerCase().includes(q)));
      const matchesMode = mode === 'all'
        || (mode === 'free' && item.is_free)
        || (mode === 'paid' && !item.is_free)
        || (mode === 'broker' && item.requires_broker_signup);
      return matchesSearch && matchesMode;
    });
  }, [items, search, mode]);

  return (
    <div className="min-h-screen bg-[#070912] text-gray-100">
      <StoreNav />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <section className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-end mb-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-semibold mb-5">
                <Package size={14} /> Expert Advisor Store
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white max-w-4xl">Download EAs with live tracking proof.</h1>
              <p className="text-gray-400 text-base md:text-lg mt-5 max-w-2xl">Free, broker-gated, and paid Expert Advisors managed directly by TrackMyFxEA. Open each EA to view conditions, features, and performance tracking link.</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
              <p className="text-sm text-gray-300 font-semibold">Store Summary</p>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <Metric label="EAs" value={items.length} />
                <Metric label="Free" value={items.filter((i) => i.is_free).length} />
                <Metric label="Paid" value={items.filter((i) => !i.is_free).length} />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-7 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative w-full md:max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search EA, symbol, strategy..." className="w-full pl-10 pr-4 py-3 bg-[#0d1117] border border-white/10 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-emerald-500/50" />
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                ['all', 'All'],
                ['free', 'Free'],
                ['broker', 'Broker gated'],
                ['paid', 'Paid'],
              ].map(([key, label]) => (
                <button key={key} onClick={() => setMode(key)} className={`px-3 py-2 rounded-lg text-xs font-semibold border ${mode === key ? 'bg-emerald-500 text-white border-emerald-400' : 'bg-[#0d1117] text-gray-400 border-white/10 hover:text-white'}`}>{label}</button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading EA store...</div>
          ) : !filtered.length ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] py-20 text-center text-gray-500">No EAs found for this filter.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((item) => <EACard key={item.id} item={item} />)}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function StoreNav() {
  return (
    <nav className="fixed top-0 w-full z-50 bg-[#070912]/90 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25"><BarChart3 size={18} className="text-white" /></div>
          <span className="text-lg font-bold text-white font-display">TrackMyFxEA</span>
        </Link>
        <div className="hidden md:flex items-center gap-6">
          <Link to="/explore" className="text-sm text-gray-400 hover:text-white">Explore</Link>
          <Link to="/features" className="text-sm text-gray-400 hover:text-white">Features</Link>
          <Link to="/how-it-works" className="text-sm text-gray-400 hover:text-white">How It Works</Link>
          <Link to="/pricing" className="text-sm text-gray-400 hover:text-white">Pricing</Link>
          <Link to="/ea-store" className="text-sm text-emerald-300">EA Store</Link>
        </div>
        <Link to="/login" className="text-sm bg-emerald-500 text-white font-semibold px-4 py-2 rounded-lg hover:bg-emerald-600">Sign In</Link>
      </div>
    </nav>
  );
}

function EACard({ item }) {
  const price = item.is_free ? 'Free' : `$${Number(item.price_usd || 0).toFixed(2)}`;
  const galleryImages = Array.isArray(item.gallery_images) ? item.gallery_images : [];
  const heroImage = galleryImages[0]?.src || item.image_url || '';
  const tags = Array.isArray(item.tags) ? item.tags : [];
  return (
    <article className="rounded-xl border border-white/10 bg-white/[0.035] overflow-hidden hover:border-emerald-500/30 transition">
      <div className="h-36 bg-[#101721] border-b border-white/10 flex items-center justify-center overflow-hidden">
        {heroImage ? <img src={heroImage} alt={item.name} className="w-full h-full object-cover" /> : <Package size={42} className="text-emerald-300" />}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-white">{item.name}</h2>
            <p className="text-xs text-gray-500 mt-1">{item.platform} {item.version}</p>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-bold ${item.is_free ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>{price}</span>
        </div>
        <p className="text-sm text-gray-400 mt-4 min-h-[42px]">{item.short_description || 'Expert Advisor for automated trading.'}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {item.requires_broker_signup && <Badge icon={Shield} text="Broker signup" />}
          {item.tracking_url && <Badge icon={BarChart3} text="Tracking proof" />}
          {tags.slice(0, 2).map((tag) => <Badge key={tag} icon={Tag} text={tag} />)}
        </div>
        <Link to={`/ea-store/${item.slug}`} className="mt-5 flex items-center justify-center gap-2 w-full rounded-lg bg-emerald-500 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-600">
          View EA <ArrowRight size={15} />
        </Link>
      </div>
    </article>
  );
}

function Badge({ icon: Icon, text }) {
  return <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-[#0d1117] px-2 py-1 text-[11px] text-gray-300"><Icon size={11} />{text}</span>;
}
function Metric({ label, value }) {
  return <div className="rounded-lg bg-[#0d1117] border border-white/10 p-3"><p className="text-[10px] uppercase text-gray-500 font-semibold">{label}</p><p className="text-xl font-bold text-white mt-1">{value}</p></div>;
}



